import React, { useEffect, useState, useContext } from 'react';
import axios from 'axios';
import { API, AuthContext } from '../App';
import Layout from '../components/Layout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Plus, ShoppingCart, Check, X } from 'lucide-react';
import { toast } from 'sonner';
import { useCurrency, formatCurrency } from '../contexts/CompanyContext';

const APPROVE = ['SUPERADMIN', 'FULL_ACCESS', 'ADMIN', 'CEO', 'MANAGER', 'ACCOUNTANT'];

export default function PurchaseManagement() {
  const { user } = useContext(AuthContext);
  const { symbol } = useCurrency();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [newPR, setNewPR] = useState({ justification: '', total_estimated: 0, item_description: '', quantity: 1 });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/purchase-requests`, { withCredentials: true });
      setItems(r.data);
    } catch { toast.error('Failed to load'); }
    setLoading(false);
  };

  const handleCreate = async () => {
    try {
      await axios.post(`${API}/purchase-requests`, {
        items: [{ description: newPR.item_description, quantity: newPR.quantity }],
        justification: newPR.justification,
        total_estimated: parseFloat(newPR.total_estimated) || 0
      }, { withCredentials: true });
      toast.success('Purchase request submitted');
      setShowNew(false);
      setNewPR({ justification: '', total_estimated: 0, item_description: '', quantity: 1 });
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const approve = async (id) => {
    try { await axios.put(`${API}/purchase-requests/${id}/approve`, {}, { withCredentials: true }); toast.success('Approved'); fetchData(); }
    catch { toast.error('Failed'); }
  };

  const reject = async (id) => {
    try { await axios.put(`${API}/purchase-requests/${id}/reject`, {}, { withCredentials: true }); toast.success('Rejected'); fetchData(); }
    catch { toast.error('Failed'); }
  };

  const statusColor = (s) => ({
    pending: 'bg-yellow-100 text-yellow-800',
    approved: 'bg-green-100 text-green-800',
    rejected: 'bg-red-100 text-red-800',
    ordered: 'bg-blue-100 text-blue-800'
  }[s] || 'bg-slate-100 text-slate-800');

  return (
    <Layout>
      <div data-testid="purchases-page" className="space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-foreground">Purchase Requests</h1>
            <p className="text-sm text-muted-foreground">Request and approve materials / services</p>
          </div>
          <Dialog open={showNew} onOpenChange={setShowNew}>
            <DialogTrigger asChild>
              <Button data-testid="new-pr-btn" className="bg-teal-600 hover:bg-teal-700"><Plus className="w-4 h-4 mr-1" />New Request</Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader><DialogTitle>Submit Purchase Request</DialogTitle></DialogHeader>
              <div className="space-y-3 mt-2">
                <div><Label>Item description</Label><Input data-testid="pr-item-input" value={newPR.item_description} onChange={(e) => setNewPR({ ...newPR, item_description: e.target.value })} placeholder="e.g., 5x HVAC air filters" /></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Quantity</Label><Input type="number" value={newPR.quantity} onChange={(e) => setNewPR({ ...newPR, quantity: parseInt(e.target.value) || 1 })} /></div>
                  <div><Label>Est. Cost ({symbol})</Label><Input type="number" value={newPR.total_estimated} onChange={(e) => setNewPR({ ...newPR, total_estimated: e.target.value })} /></div>
                </div>
                <div><Label>Justification</Label><Textarea rows={3} value={newPR.justification} onChange={(e) => setNewPR({ ...newPR, justification: e.target.value })} /></div>
                <Button data-testid="submit-pr-btn" onClick={handleCreate} disabled={!newPR.item_description || !newPR.justification} className="w-full bg-teal-600 hover:bg-teal-700">Submit</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? <div className="py-12 text-center text-muted-foreground">Loading...</div> : (
          <div className="space-y-3">
            {items.map((pr) => (
              <Card key={pr.id} data-testid={`pr-card-${pr.id}`} className="p-4">
                <div className="flex flex-col sm:flex-row justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <ShoppingCart className="w-4 h-4 text-teal-600" />
                      <h3 className="font-semibold text-foreground">{pr.items?.[0]?.description || 'Purchase Request'}</h3>
                    </div>
                    <p className="text-sm text-muted-foreground mb-2">{pr.justification}</p>
                    <div className="flex flex-wrap gap-2 text-xs">
                      <span className="px-2 py-1 bg-slate-100 rounded-full">Qty: {pr.items?.[0]?.quantity || 1}</span>
                      <span className="px-2 py-1 bg-slate-100 rounded-full">{formatCurrency(pr.total_estimated, symbol)}</span>
                      <span className={`px-2 py-1 rounded-full font-semibold ${statusColor(pr.status)}`}>{pr.status}</span>
                    </div>
                  </div>
                  {pr.status === 'pending' && APPROVE.includes(user?.role) && (
                    <div className="flex gap-2">
                      <Button data-testid={`approve-pr-${pr.id}`} size="sm" onClick={() => approve(pr.id)} className="bg-green-600 hover:bg-green-700"><Check className="w-4 h-4 mr-1" />Approve</Button>
                      <Button data-testid={`reject-pr-${pr.id}`} size="sm" onClick={() => reject(pr.id)} variant="outline" className="text-red-600 border-red-600"><X className="w-4 h-4 mr-1" />Reject</Button>
                    </div>
                  )}
                </div>
              </Card>
            ))}
            {items.length === 0 && (
              <Card className="p-10 text-center">
                <ShoppingCart className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                <p className="text-muted-foreground">No purchase requests</p>
              </Card>
            )}
          </div>
        )}
      </div>
    </Layout>
  );
}
